import { BreakpointObserver } from '@angular/cdk/layout';
import { Inject, Injectable, Signal, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { BadgeNotifierService } from '@polaris-customerweb/sharedcomponents.garage';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { LoginCreateAccountComponent } from 'src/app/components/account/login-create-account/login-create-account.component';
import { WINDOW } from '../app-config/factories/window.factory';
import { TokenService } from './token.service';
import { SetIsAuthenticated } from './user-auth.actions';

@Injectable({ providedIn: 'root' })
export class UserAuthService {
    $isAuthenticated: Signal<boolean>;
    $isAuthenticating: Signal<boolean>;
    /** @description Observable that emits once auth status is known. */
    isAuthenticated$: Observable<boolean>;
    /** @description Observable that emits once auth status is known and user is authenticated. */
    /** @deprecated Use isAuthenticated and return a default value if not. */
    whenAuthenticated$: Observable<void>;

    private isAuthenticatedSubject = new BehaviorSubject<boolean | undefined>(
        undefined
    );
    private _$isAuthenticated = signal<boolean>(false);
    private _$isAuthenticating = signal<boolean>(true);

    constructor(
        private configService: ConfigService,
        private badgeNotifierService: BadgeNotifierService,
        private breakpointObserver: BreakpointObserver,
        private matDialog: MatDialog,
        private routingHelpersService: RoutingHelpersService,
        private store: Store,
        private tokenService: TokenService,
        @Inject(WINDOW) private window: Window
    ) {
        this.$isAuthenticated = this._$isAuthenticated.asReadonly();
        this.$isAuthenticating = this._$isAuthenticating.asReadonly();
        this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable().pipe(
            filter((isAuthenticated) => typeof isAuthenticated === 'boolean'),
            take(1)
        );
        this.whenAuthenticated$ = this.isAuthenticated$.pipe(
            filter((isAuthenticated) => isAuthenticated),
            map(() => undefined)
        );
    }

    /**
     * @returns Observable that emits auth token and then completes.
     */
    getAuthToken$(): Observable<string> {
        return from(this.getAuthToken());
    }

    async getAuthToken(): Promise<string> {
        let token: string;

        try {
            token = await this.tokenService.getToken();
        } catch {
            token = '';
        }

        const isAuthenticated = !this.tokenService.isTokenExpired(token);

        this._$isAuthenticating.set(false);
        this._$isAuthenticated.set(isAuthenticated);
        this.isAuthenticatedSubject.next(isAuthenticated);
        this.store.dispatch(SetIsAuthenticated({ isAuthenticated }));

        return token;
    }

    async isFreshlyAuthenticated(): Promise<boolean> {
        const token = await this.getAuthToken();
        return !this.tokenService.isTokenStale(token, 180);
    }

    async isRecentlyAuthenticated(): Promise<boolean> {
        const token = await this.getAuthToken();
        return !this.tokenService.isTokenStale(token);
    }

    logIn(
        email?: string,
        returnToUrl?: string,
        initialScreen?: 'signUp' | 'login',
        forceLogin = false
    ): void {
        if (this.configService.isEmbedded) {
            const paramList: string[] = [];
            let params = '';

            if (email) {
                paramList.push(`email=${email}`);
            }

            if (returnToUrl) {
                if (!returnToUrl.includes('http')) {
                    const cultureCode = `/${this.configService.cultureCode}/`;
                    if (
                        this.configService.siteUrl.endsWith(cultureCode) &&
                        returnToUrl.startsWith(cultureCode)
                    ) {
                        returnToUrl = returnToUrl.replace(
                            `/${this.configService.cultureCode}`,
                            ''
                        );
                    }

                    returnToUrl = this.routingHelpersService.combineUrl(
                        this.configService.siteUrl,
                        returnToUrl
                    );
                }
                paramList.push(`returnUrl=${encodeURIComponent(returnToUrl)}`);
            }
            if (initialScreen && initialScreen === 'signUp') {
                paramList.push(`register=1`);
            }
            if (forceLogin) {
                paramList.push('prompt=login');
                paramList.push('max_age=0');
            }
            if (paramList.length > 0) {
                params = `?${paramList.join('&')}`;
            }
            const loginUrl = this.routingHelpersService.combineUrl(
                this.configService.siteUrl,
                'login/'
            );
            this.window.location.href = `${loginUrl}${params}`;
        } else {
            if (typeof this.window.RIDE_COMMAND.logIn === 'function') {
                this.window.RIDE_COMMAND.logIn(returnToUrl);
            }
        }
    }

    logOut(): void {
        if (this.configService.isEmbedded) {
            this.window.location.href = this.routingHelpersService.combineUrl(
                this.configService.siteUrl,
                'logout/'
            );
        } else {
            if (typeof this.window.RIDE_COMMAND.logOut === 'function') {
                this.window.RIDE_COMMAND.logOut();
            }
        }
    }

    promptAuthenticationAndNavigate(
        navigateTo?: string,
        title?: string,
        bodytext?: string
    ): void {
        const data: LoginCreateAccountDialog = {
            bodytext,
            navigateTo,
            title,
        };
        const isSmallScreen =
            this.breakpointObserver.isMatched('(max-width: 959px)');
        let height = '';
        if (!isSmallScreen) {
            height = 'auto';
        }
        this.matDialog.open(LoginCreateAccountComponent, {
            data,
            height,
            panelClass: 'login-create-dialog',
        });
    }

    /**
     * Updates whether the Vehicle Settings lock is authenticated
     * @modifies BadgeNotifier VehicleSettings badge status
     * @param token the JWT to determine badge status from
     * @returns whether the Vehicle Settings lock is authenticated
     */
    updateBadgeStatus(token: string): boolean {
        const isAuthenticationStale = this.tokenService.isTokenStale(token);

        this.badgeNotifierService.setBadgeStatus(
            'VehicleSettings',
            !isAuthenticationStale
        );

        return !isAuthenticationStale;
    }
}

export interface LoginCreateAccountDialog {
    navigateTo?: string;
    title?: string;
    bodytext?: string;
}
